<template>
  <div class="atlas-ingestion-upload-panel">
    <section class="atlas-ingestion-upload-panel__section">
      <div v-if="!uploadingStarted && !mergeMode && !requiresFolderRebind" class="atlas-ingestion-upload-panel__ready-banner">
        <span class="atlas-ingestion-upload-panel__ready-banner-icon">
          <IconEmbedded name="save_2" :size="22" color="#118689" />
        </span>
        <div class="atlas-ingestion-upload-panel__ready-banner-text">
          <template v-if="totalImagesToUpload > 0">
            <span class="atlas-ingestion-upload-panel__ready-banner-title">{{ totalImagesToUpload.toLocaleString() }} images ready to upload</span>
            <span class="atlas-ingestion-upload-panel__ready-banner-sub">Across {{ groups.length }} groups</span>
          </template>
          <template v-else-if="unmatchedGroups.length > 0">
            <span class="atlas-ingestion-upload-panel__ready-banner-title">No images queued for upload yet</span>
            <span class="atlas-ingestion-upload-panel__ready-banner-sub">
              {{ unmatchedFilesCount.toLocaleString() }} unmatched file(s) below — add them as a group to include them, or analyze again.
            </span>
          </template>
          <template v-else>
            <span class="atlas-ingestion-upload-panel__ready-banner-title">0 images ready to upload</span>
            <span class="atlas-ingestion-upload-panel__ready-banner-sub">Across {{ groups.length }} groups</span>
          </template>
        </div>
        <SrpButton
          v-if="canMergeGroups"
          class="atlas-ingestion-upload-panel__ready-banner-merge-btn"
          size="small"
          color="gray"
          fill="outlined"
          :isDisabled="isAnalyzing || isUploading"
          @click="enterMergeSelectionMode"
        >
          <template #icon><IconEmbedded name="merge_2" :size="16" /></template>
          Merge groups
        </SrpButton>
      </div>
      <div v-else-if="mergeMode" class="atlas-ingestion-upload-panel__merge-banner">
        <span class="atlas-ingestion-upload-panel__merge-banner-icon" aria-hidden="true">
          <IconEmbedded name="merge_2" :size="22" color="#118689" />
        </span>
        <div class="atlas-ingestion-upload-panel__merge-banner-text">
          <span class="atlas-ingestion-upload-panel__merge-banner-title">Select groups to merge</span>
          <span class="atlas-ingestion-upload-panel__merge-banner-sub">{{ selectedGroupKeys.length }} selected</span>
        </div>
        <div class="atlas-ingestion-upload-panel__merge-banner-actions">
          <SrpButton size="small" color="gray" fill="outlined" :isDisabled="isAnalyzing || isUploading" @click="cancelMergeMode"> Cancel </SrpButton>
          <SrpButton size="small" color="teal" :isDisabled="isAnalyzing || isUploading || selectedGroupKeys.length < 2" @click="confirmMerge">
            Merge selected ({{ selectedGroupKeys.length }})
          </SrpButton>
        </div>
      </div>
    </section>

    <section v-if="groups.length || unmatchedGroups.length > 0" class="atlas-ingestion-upload-panel__section">
      <template v-if="showLibraryProgressCard">
        <AtlasIngestionLibraryProgress
          :overallPercent="libraryOverallPercent"
          :currentGroupName="libraryCurrentGroupName"
          :currentGroupUploadedCount="libraryCurrentGroupUploadedCount"
          :currentGroupTotalCount="libraryCurrentGroupTotalCount"
          :uploadedImagesCount="libraryUploadedImagesCount"
          :totalImagesToUpload="totalImagesToUpload"
          :completedGroupsCount="libraryCompletedGroupsCount"
          :totalGroupsCount="libraryTotalGroupsCount"
          :secondsRemainingApprox="librarySecondsRemainingApprox"
        />
        <div class="atlas-ingestion-upload-panel__groups-heading-row atlas-ingestion-upload-panel__groups-heading-row--after-progress">
          <h4 class="global-h4 atlas-ingestion-upload-panel__group-progress-heading">Group progress</h4>
          <InfoIconWithTooltip v-if="analysisNotes" class="atlas-ingestion-upload-panel__analysis-notes-tooltip" :width="320" :iconSize="15">
            <span class="atlas-ingestion-upload-panel__tooltip-block">{{ analysisNotes }}</span>
          </InfoIconWithTooltip>
        </div>
      </template>
      <div v-else class="atlas-ingestion-upload-panel__groups-heading-row">
        <h3 class="global-h4 atlas-ingestion-upload-panel__groups-heading-title">{{ groupsSectionHeading }}</h3>
        <InfoIconWithTooltip v-if="analysisNotes" class="atlas-ingestion-upload-panel__analysis-notes-tooltip" :width="320" :iconSize="15">
          <span class="atlas-ingestion-upload-panel__tooltip-block">{{ analysisNotes }}</span>
        </InfoIconWithTooltip>
      </div>
      <AtlasIngestionGroupCard
        v-for="group in groups"
        :key="group.groupKey"
        :group="group"
        :folderLabel="folderLabel"
        :uploadingStarted="uploadingStarted"
        :isSelectable="mergeMode"
        :isSelected="isGroupSelected(group.groupKey)"
        :isPreviewExpanded="expandedGroupsByKey[group.groupKey] === true"
        @skip="emit('removeGroup', group.groupKey)"
        @togglePreview="emit('togglePreview', group.groupKey)"
        @toggleSelected="toggleGroupSelected(group.groupKey)"
        @rename="emit('renameGroup', group.groupKey, $event)"
        @persist="emit('persist')"
      />
    </section>

    <section v-if="groups.length || unmatchedGroups.length > 0" class="atlas-ingestion-upload-panel__section atlas-ingestion-upload-panel__section--actions-floating">
      <NoteWithIcon v-if="limitErrorMessage" class="atlas-ingestion-upload-panel__limit-note" color="red" icon="warning_2" role="alert">
        <template #text>
          <p
            v-for="(line, i) in limitErrorMessage"
            :key="i"
            class="global-p atlas-ingestion-upload-panel__limit-note-line"
            :class="{ 'atlas-ingestion-upload-panel__limit-note-line--followup': i > 0 }"
          >
            {{ line }}
          </p>
        </template>
      </NoteWithIcon>
      <div
        v-if="requiresFolderRebind && !allGroupsUploadComplete"
        class="atlas-ingestion-upload-panel__notice atlas-ingestion-upload-panel__notice--warning atlas-ingestion-upload-panel__session-restored-notice"
      >
        Session restored from browser storage. Select the same folder again before uploading.
      </div>
      <div class="atlas-ingestion-upload-panel__actions">
        <div class="atlas-ingestion-upload-panel__actions-bar">
          <div class="atlas-ingestion-upload-panel__action-button-row">
            <SrpButton v-if="requiresFolderRebind && !allGroupsUploadComplete" :isDisabled="isAnalyzing || isUploading || mergeMode" @click="emit('selectFolder')">
              <template #icon><IconEmbedded name="folder_2" :size="17" /></template>
              Select Folder
            </SrpButton>
            <SrpButton v-else color="teal" :isDisabled="beginUploadButtonDisabled || mergeMode" @click="emit('uploadAll')">
              <template #icon><IconEmbedded name="upload_2" :size="17" /></template>
              {{ beginUploadButtonLabel }}
            </SrpButton>
            <SrpButton color="gray" fill="outlined" :isDisabled="isAnalyzing || isUploading || mergeMode" @click="emit('back')">
              <template #icon><IconEmbedded name="arrow-left_2" :size="17" /></template>
              Back
            </SrpButton>
          </div>
          <div v-if="$slots.actionsTrailing" class="atlas-ingestion-upload-panel__actions-trailing">
            <slot name="actionsTrailing" />
          </div>
        </div>
      </div>
    </section>

    <section v-if="unmatchedGroups.length > 0 && !mergeMode" class="atlas-ingestion-upload-panel__section atlas-ingestion-upload-panel__section--unmatched">
      <div class="atlas-ingestion-upload-panel__unmatched-heading-row">
        <h4 class="global-h4 atlas-ingestion-upload-panel__unmatched-heading">Excluded from upload</h4>
      </div>
      <p class="global-p atlas-ingestion-upload-panel__unmatched-note">These {{ unmatchedFilesCount }} files are not currently marked for upload.</p>
      <AtlasIngestionGroupCard
        v-for="group in unmatchedGroups"
        :key="group.groupKey"
        :group="group"
        :folderLabel="folderLabel"
        :uploadingStarted="uploadingStarted"
        :isSelectable="false"
        :isSelected="false"
        :isPreviewExpanded="expandedGroupsByKey[group.groupKey] === true"
        isUnmatched
        @togglePreview="emit('togglePreview', group.groupKey)"
        @promote="emit('promoteUnmatched', group.groupKey)"
        @persist="emit('persist')"
      />
    </section>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import NoteWithIcon from "@components/NoteWithIcon.vue";
import SrpButton from "@components/ui/SrpButton.vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import InfoIconWithTooltip from "@components/ui/InfoIconWithTooltip.vue";
import AtlasIngestionGroupCard, { type AtlasIngestionEditableGroup } from "@components/ContentCalendar/AtlasIngestionGroupCard.vue";
import AtlasIngestionLibraryProgress from "@components/ContentCalendar/AtlasIngestionLibraryProgress.vue";

defineOptions({
  name: "AtlasIngestionUploadPanel",
});

const props = defineProps<{
  groups: Array<AtlasIngestionEditableGroup>;
  /** Groups shown separately and excluded from upload until promoted. */
  unmatchedGroups: Array<AtlasIngestionEditableGroup>;
  folderLabel: string;
  analysisNotes: string | null;
  isAnalyzing: boolean;
  isUploading: boolean;
  uploadingStarted: boolean;
  requiresFolderRebind: boolean;
  allGroupsUploadComplete: boolean;
  beginUploadButtonDisabled: boolean;
  beginUploadButtonLabel: string;
  groupsSectionHeading: string;
  libraryOverallPercent: number;
  libraryUploadedImagesCount: number;
  libraryCurrentGroupName: string;
  libraryCurrentGroupUploadedCount: number;
  libraryCurrentGroupTotalCount: number;
  libraryCompletedGroupsCount: number;
  libraryTotalGroupsCount: number;
  librarySecondsRemainingApprox: number | null;
  /** When set, Begin Upload is disabled from the parent and this message explains why. */
  limitErrorMessage: ReadonlyArray<string> | null;
  expandedGroupsByKey: Record<string, boolean>;
}>();

const emit = defineEmits<{
  selectFolder: [];
  uploadAll: [];
  back: [];
  removeGroup: [groupKey: string];
  togglePreview: [groupKey: string];
  renameGroup: [groupKey: string, name: string];
  mergeGroups: [groupKeys: string[]];
  promoteUnmatched: [groupKey: string];
  persist: [];
}>();

const mergeMode = ref(false);
const selectedGroupKeys = ref<string[]>([]);

const totalImagesToUpload = computed(() => props.groups.reduce((sum, g) => sum + g.filePaths.length, 0));
const unmatchedFilesCount = computed(() => props.unmatchedGroups.reduce((sum, g) => sum + g.filePaths.length, 0));

const showLibraryProgressCard = computed(() => props.uploadingStarted && !props.allGroupsUploadComplete);

const canMergeGroups = computed(() => !props.uploadingStarted && !props.requiresFolderRebind && props.groups.length >= 2 && !props.isAnalyzing && !props.isUploading);

function enterMergeSelectionMode() {
  mergeMode.value = true;
  selectedGroupKeys.value = [];
}

function cancelMergeMode() {
  mergeMode.value = false;
  selectedGroupKeys.value = [];
}

function isGroupSelected(groupKey: string): boolean {
  return selectedGroupKeys.value.includes(groupKey);
}

function toggleGroupSelected(groupKey: string) {
  const idx = selectedGroupKeys.value.indexOf(groupKey);
  if (idx >= 0) {
    selectedGroupKeys.value = selectedGroupKeys.value.filter(k => k !== groupKey);
  } else {
    selectedGroupKeys.value = [...selectedGroupKeys.value, groupKey];
  }
}

function confirmMerge() {
  if (selectedGroupKeys.value.length < 2) {
    return;
  }
  emit("mergeGroups", [...selectedGroupKeys.value]);
  cancelMergeMode();
}
</script>

<style scoped lang="scss">
.atlas-ingestion-upload-panel {
  &__section {
    margin-top: 20px;
    margin-bottom: 35px;
  }

  &__section--actions-floating {
    position: sticky;
    inset: auto auto 15px 0;
    z-index: 2;
    margin-top: 20px;
    padding-top: 6px;
    background: rgba(255, 255, 255, 1);
    box-shadow: 0 -15px 20px 20px rgba(255, 255, 255, 1);
  }

  &__notice {
    margin-bottom: 12px;
    padding: 10px 12px;
    border-radius: 6px;
    font-size: 14px;

    &--warning {
      background: rgba(255, 193, 7, 0.16);
    }
  }

  &__session-restored-notice {
    margin-top: 0;
    margin-bottom: 0;
  }

  &__session-restored-notice + &__actions {
    margin-top: 12px;
  }

  &__limit-note {
    margin-bottom: 12px;
  }

  &__limit-note-line {
    margin: 0;
    line-height: 1.45;
  }

  &__limit-note-line--followup {
    margin-top: 8px;
  }

  &__actions {
    margin-top: 20px;
    width: 100%;
  }

  &__actions-bar {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    flex-wrap: wrap;
    gap: 12px;
    width: 100%;
  }

  &__action-button-row {
    display: flex;
    gap: 10px;
    align-items: center;
    flex-wrap: wrap;
    flex: 0 1 auto;
  }

  &__actions-trailing {
    margin-left: auto;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    flex-shrink: 0;

    :deep(.super-user-action-panel) {
      width: auto;
    }
  }

  &__ready-banner {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 14px 16px;
    margin-bottom: 0;
    border-radius: 10px;
    background: rgb(206, 228, 200);
  }

  &__ready-banner-icon {
    flex-shrink: 0;
    width: 40px;
    height: 40px;
    border-radius: 8px;
    background: rgba(44, 117, 23, 0.12);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
  }

  &__ready-banner-text {
    flex: 1 1 auto;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  &__ready-banner-title {
    font-weight: 700;
    font-size: 16px;
    line-height: 1.3;
    color: #1a1a1a;
    word-break: break-word;
  }

  &__ready-banner-sub {
    font-size: 14px;
    line-height: 1.4;
    color: var(--srp-color-text-muted, #666);
  }

  &__ready-banner-merge-btn {
    flex-shrink: 0;
    align-self: center;
  }

  &__merge-banner {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 14px 16px;
    border-radius: 10px;
    background: rgb(206, 228, 200);
    flex-wrap: wrap;
  }

  &__merge-banner-icon {
    flex-shrink: 0;
    width: 40px;
    height: 40px;
    border-radius: 8px;
    background: #fff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
  }

  &__merge-banner-text {
    flex: 1 1 auto;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  &__merge-banner-title {
    font-weight: 700;
    font-size: 16px;
    line-height: 1.3;
    color: #1a1a1a;
  }

  &__merge-banner-sub {
    font-size: 14px;
    line-height: 1.4;
    color: var(--srp-color-text-muted, #666);
  }

  &__merge-banner-actions {
    display: flex;
    gap: 10px;
    align-items: center;
    flex-shrink: 0;
    margin-left: auto;
  }

  &__groups-heading-row {
    display: flex;
    align-items: center;
    // justify-content: space-between;
    gap: 12px;
  }

  &__groups-heading-title {
    margin: 0;
  }

  &__groups-heading-row--after-progress {
    margin-top: 16px;
  }

  &__group-progress-heading {
    margin: 0;
    font-size: 15px;
    font-weight: 700;
    color: #1a1a1a;
  }

  &__analysis-notes-tooltip {
    flex-shrink: 0;
  }

  &__tooltip-block {
    display: block;
    font-size: 13px;
    line-height: 1.45;
    white-space: pre-wrap;
  }

  &__section--unmatched {
    margin-top: 24px;
    padding-top: 20px;
    border-top: 1px solid var(--srp-color-border-subtle, #dee2e6);
  }

  &__unmatched-heading-row {
    margin-bottom: 6px;
  }

  &__unmatched-heading {
    margin: 0;
    color: var(--srp-color-text-muted, #555);
  }

  &__unmatched-note {
    margin: 0 0 12px;
    font-size: 14px;
    line-height: 1.45;
    color: var(--srp-color-text-muted, #666);
  }
}
</style>
